import { ArrowRightIcon, Bars3Icon, UserIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType, logout, signInRequest, USER_LOGIN_PAGE_MODAL_VIEWED } from 'actions'
import cloneDeep from 'clone-deep'
import { AssistantModal } from 'components/AssistantModal'
import { GlobalLoanSearch } from 'components/GlobalLoanSearch'
import { GlobalUserSwitch } from 'components/GlobalUserSwitch'
import { HeaderMenu } from 'components/HeaderMenu'
import { HeaderNav, HeaderNavItemType } from 'components/HeaderNav'
import { LoanFieldLookup } from 'components/LoanFieldLookup'
import { LogoBlue } from 'components/Logo'
import { LogInAlertModal, ResetPasswordModal, TfaModal } from 'components/Modals'
import { NotificationBell } from 'components/NotificationBell'
import { PipelineModeSwitch } from 'components/PipelineModeSwitch'
import { COMPANY_NAME, COMPANY_WEBSITE_URL, IS_MAIN_COMPANY } from 'config'
import { hasPagePermissions } from 'hooks/hasPagePermissions'
import { usePermissions } from 'hooks/usePermissions'
import { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from 'react-query'
import { connect, useDispatch, useSelector } from 'react-redux'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import { getLoginPageModal, isUsioSelected, stopImpersonate } from 'services'
import { getNavBarData } from 'services/apis/admin'
import { svgLoading } from 'stories/assets'
import { Input } from 'stories/components'
import { convertUrl, delay, validateEmail } from 'utils'
import { applyImpersonationSession } from 'utils/applyImpersonationSession'
import { redirectAfterImpersonationIfNeeded } from 'utils/impersonationNavigation'

import { renderNav } from '../utils'

interface menuType {
  label: String
  visible: Boolean | undefined
  href: any
  external?: boolean
  /** Present on the configurable rows; the rule before Sign Out has neither. */
  key?: string
  order?: number
}

// Public release-notice page (HubSpot). Opens in a new tab from the profile menu.
const RELEASE_NOTES_URL = 'https://releases.nexys.com/product-update-2026-08-08'

type tpNavigation = {
  [key: string]: any
}

const routeMap: Record<string, string> = {
  '/pipeline': 'pipeline',
  '/resources': 'resources',
  '/reminders': 'reminders',
  '/reports': 'reports',
  '/reports/servicing': 'reports',
  '/tasks': 'task',
  '/ai-projects': 'aiProjects',
  '/appraisals': 'appraisals',
  '/new': 'bridgequal',
  '/register': 'register',
}

function signedInName(auth: any) {
  const session = auth?.role?.isBrokerChild ? auth.role : auth?.profile?.isBrokerChild ? auth.profile : null
  if (session) return session.brokerChildName || session.brokerChildEmail || ''
  return auth?.profile?.name || ''
}

function _Header(props: any) {
  const [menus, setMenus] = useState<menuType[]>([])
  const {
    auth,
    menu: _menu,
    pagePermissions,
    notificationsEnabled,
    usioSelected,
  } = useSelector((state: any) => ({
    auth: state.auth,
    menu: state.header.menu || {},
    pagePermissions: state.globalConfigReducer[GlobalConfigType.PagePermissions],
    // Admin Tools > Configuration > Global Settings > Views > Notifications. Off by default, and
    // off while the config is still loading, so the bell never flashes in for companies without it.
    notificationsEnabled: !!state.globalConfigReducer[GlobalConfigType.GlobalSettingV2]?.notificationsEnabled,
    // USIO picked anywhere on Rules Configuration > Payment Methods shows the USIO page in the menu.
    usioSelected: isUsioSelected(state.globalConfigReducer[GlobalConfigType.RulesConfig] || {}),
  }))
  const { data: permissionData, hasPermission } = usePermissions()

  const [navigations, setNavigations] = useState<tpNavigation>({})
  const [logInAlerts, setLogInAlerts] = useState<Array<any>>([])
  const [is2fa, set2fa] = useState(false)
  const [phone, setPhone] = useState('')
  const [hasPasskey, setHasPasskey] = useState(false)
  const [action, setAction] = useState('')
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false)
  const [companyBroadcast, setCompanyBroadcast] = useState(null)
  const [activeTasks, setActiveTasks] = useState(0)
  const [activeAppraisals, setActiveAppraisals] = useState(0)
  const [activeReminders, setActiveReminders] = useState(0)
  const [showMobileMenu, setShowMobileMenu] = useState(false)

  const location: any = useLocation()
  const navigate = useHistory()

  const { header } = useSelector((state: any) => {
    return {
      header: state.header,
    }
  })
  const reportsOverride = header?.reportsOverride
  const servicingMenu = header?.servicingMenu

  // Broker Portal is the top menu in Origination mode and Servicing Portal is the top menu in
  // Servicing mode: each is one complete list, so a company orders Pipeline, Reports, Tasks and
  // the servicing screens freely against each other instead of the servicing block being pinned
  // behind Reports. `servicingMenu` arriving in the store is what tells us the mode.
  const isServicingMode = auth.isAuthenticated && Array.isArray(servicingMenu)

  // The account dropdown's own section, not a portal: it is the same list in both modes.
  const accountMenuConfig = useMemo(() => _menu?.accountMenu || {}, [_menu])

  // The one thing the config cannot express: the permission each servicing screen's route is
  // already gated on, so the link is only offered to someone the screen will open for.
  const servicingPermissions = useMemo(() => {
    const permissions: Record<string, string | undefined> = {}
    if (Array.isArray(servicingMenu))
      servicingMenu.forEach((sm: any) => {
        if (sm?.key) permissions[sm.key] = sm.permission
      })
    return permissions
  }, [servicingMenu])

  const menu = useMemo(() => {
    if ((location.pathname === '/new' || location.pathname.includes('/newpa')) && !auth.isAuthenticated)
      return _menu?.quickAppPortal || {}
    if (isServicingMode) return _menu?.servicingPortal || {}
    return (auth.isAuthenticated ? _menu?.brokerPortal : _menu?.homePortal) || {}
  }, [_menu, auth, location.pathname, isServicingMode])

  const activeMenu = (to: String) => {
    if (location.pathname === to) {
      return 'bg-shade-blue/10 text-shade-blue font-semibold'
    }
    return ''
  }

  // The top-header links, in order. `visible` is the permission answer and `menu[key].value`
  // the tenant's own switch; every item in either mode now has both. HeaderNav decides how
  // many of these fit on the row.
  const navItems: HeaderNavItemType[] = useMemo(
    () =>
      Object.keys(navigations)
        .filter((key) => navigations[key].visible && menu[key]?.value)
        .map((key) => ({
          key,
          name: navigations[key].name,
          href: navigations[key].href,
          active: location.pathname === navigations[key].href,
          className: navigations[key].className,
        })),
    [navigations, menu, location.pathname],
  )

  const [fields, setFields] = useState({
    email: '',
    password: '',
  })

  const dispatch = useDispatch()

  const handleLogout = () => {
    const { temparary, isBorrower, isInvestor } = auth.profile
    dispatch(logout())
    if (temparary && IS_MAIN_COMPANY) window.open(`https://${COMPANY_WEBSITE_URL}`, '_self')
    else if (isBorrower) navigate.push('/borrowerlogin')
    else if (isInvestor) navigate.push('/investorPipelines/signin')
    else navigate.push('/')
  }

  const getNotificationCN = (size: number) => {
    return size >= 100 ? 'w-10 h-5 top-[-10px] right-[-28px]' : 'w-6 h-4 top-[-10px] right-[-18px]'
  }

  // Update navigations whenever menu or notifications change
  useEffect(() => {
    const draft: tpNavigation = {}

    // Process menu items
    Object.entries(menu).forEach(([key, item]: [string, any]) => {
      if (item) {
        draft[key] = {
          name: item.title || key,
          href: item.link || `/${key.toLowerCase()}`,
          visible: item.value,
          order: item.order || 999,
          ...(menu[key]?.background && {
            className: 'bg-shade-blue rounded text-white hover:bg-white',
          }),
        }
      }
    })

    // Update visibility based on permissions and authentication
    Object.entries(draft).forEach(([key, item]) => {
      const page = routeMap[draft[key].href]
      if (page === 'reports') {
        item.visible = Boolean(hasPermission('Reports') && hasPagePermissions(['reports']))
        // Origination and servicing reports are tabs on one page now, so the entry is never
        // renamed per mode. An explicit override set elsewhere is still honoured.
        if (reportsOverride) {
          item.name = reportsOverride.name ?? item.name
          item.href = reportsOverride.href ?? item.href
        }
      } else if (page === 'resources') {
        item.visible = Boolean(hasPagePermissions(['resources']))
      } else if (page === 'reminders') {
        item.visible = Boolean(hasPermission('Reminders') && hasPagePermissions(['reminders']))
      } else if (page === 'appraisals') {
        item.visible = Boolean(hasPermission('Loan.Appraisals') && hasPagePermissions(['appraisals']))
      } else if (page === 'task') {
        item.visible = Boolean(hasPermission('Task') && hasPagePermissions(['tasks']))
      } else if (page === 'bridgequal' && auth.isAuthenticated) {
        item.visible = Boolean(hasPermission('Loan.QuickApp'))
      } else if (key in servicingPermissions) {
        const permission = servicingPermissions[key]
        item.visible = permission ? Boolean(hasPermission(permission)) : true
      } else if (key === 'usioPortal') {
        // The merchant portal is only there for a company that has one selected.
        item.visible = Boolean(usioSelected && hasPermission('UsioPortal'))
      }

      // Update visibility based on authentication state
      if (auth.isAuthenticated) {
        if (['home', 'about_us', 'investors', 'how_it_works', 'where_we_lend', 'contacts'].includes(key)) {
          item.visible = false
        }
        if (page === 'pipeline') item.visible = true
        if (page === 'register') item.visible = false
      } else {
        if (['pipeline', 'reports', 'reminders', 'appraisals', 'task'].includes(page)) {
          item.visible = false
        }
        if (key === 'home') item.visible = !IS_MAIN_COMPANY
        if (['about_us', 'investors', 'how_it_works', 'where_we_lend', 'contacts'].includes(key)) {
          item.visible = IS_MAIN_COMPANY
        }
        if (page === 'bridgequal') item.visible = true
        if (page === 'register') item.visible = true
      }
    })

    // Handle special cases for notifications
    const notificationItems = [
      { key: 'task', label: menu.task?.title || 'Tasks', count: activeTasks },
      { key: 'appraisals', label: menu.appraisals?.title || 'Appraisals', count: activeAppraisals },
      { key: 'reminders', label: menu.reminders?.title || 'Reminders', count: activeReminders },
    ]

    notificationItems.forEach(({ key, label, count }) => {
      let link: string = ''
      Object.keys(routeMap).map((item) => {
        if (routeMap[item] === key) link = item
      })
      let _key: string = ''
      Object.keys(draft).map((item) => {
        if (draft[item].href === link) _key = item
      })
      const item = draft[_key]
      if (item?.visible && count) {
        item.name = `
          <div class="relative">
            ${label}<div class="absolute ${getNotificationCN(
          count,
        )} text-sm flex items-center justify-center text-white rounded-full bg-red-700">${count}</div>
          </div>
        `
      } else if (item?.visible) {
        item.name = label
      }
    })

    // Sort the navigations based on order value
    const sortedNavigations = Object.fromEntries(
      Object.entries(draft).sort(([, a], [, b]) => (a.order || 999) - (b.order || 999)),
    )
    setNavigations(sortedNavigations)
  }, [
    menu,
    auth.isAuthenticated,
    IS_MAIN_COMPANY,
    hasPermission,
    hasPagePermissions,
    activeTasks,
    activeAppraisals,
    activeReminders,
    reportsOverride,
    servicingPermissions,
    usioSelected,
    location.pathname,
  ])

  useEffect(() => {
    if (!auth.isAuthenticated) {
      setCompanyBroadcast(null)
    } else {
      updateActiveNotification()
    }
    // `location` was in here, so every in-app navigation refetched the navbar - 24 times during
    // one servicing screen load. The effect just above deliberately depends on `location.pathname`
    // rather than the object, and this one should not depend on either: the navbar counts do not
    // vary by route. `header.activeChangeFlag` is the explicit refresh signal (dispatched when a
    // task or appraisal changes) and the visibilitychange listener below covers returning to the
    // tab, so nothing needs a route change to stay current.
  }, [auth.isAuthenticated, header.activeChangeFlag])

  useEffect(() => {
    if (auth.isAuthenticated) {
      getLoginPageModal().then((res) => {
        setLogInAlerts(res)
      })
      const showPasswordWarning = localStorage.getItem('showPasswordWarning')
      if (auth.warningPwChange && !showPasswordWarning && !auth.requirePwChange) {
        toast(
          'Your password has expired. For your security, please update it at your earliest convenience to regain access.',
          { type: 'warning' },
        )
        localStorage.setItem('showPasswordWarning', 'true')
      }
    } else {
      localStorage.removeItem('showPasswordWarning')
    }
  }, [auth.isAuthenticated, auth.warningPwChange, auth.requirePwChange])

  const updateActiveNotification = () => {
    if (auth.profile.temparary !== true && auth.isAuthenticated) {
      getNavBarData().then((res) => {
        setCompanyBroadcast(res.broadcast)
        setActiveTasks(Number(res.activeTasks))
        setActiveAppraisals(Number(res.activeAppraisals))
        setActiveReminders(Number(res.activeReminders))
      })
    }
  }

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        updateActiveNotification()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  useEffect(() => {
    // Build from persisted auth.role via hasPermission fallback — do not wait for react-query,
    // or visible:true items (Edit Profile, etc.) stay missing after a hard refresh until
    // /user/permissions returns.
    const menuConfig = [
      {
        key: 'whatsNew',
        label: "What's New",
        href: RELEASE_NOTES_URL,
        external: true,
        visible: true,
      },
      {
        key: 'registrations',
        label: 'Manage Accounts',
        href: '/registrations',
        permissions: ['User'],
        pages: ['manage_users', 'registrations'],
      },
      {
        key: 'teams',
        label: 'Manage Teams',
        href: '/teams',
        permissions: ['Team'],
        pages: ['manage_users', 'teams'],
      },
      {
        key: 'borrowers',
        label: 'Manage Borrowers',
        href: '/borrowers',
        permissions: ['Borrowers'],
        pages: ['manage_users', 'borrowers'],
      },
      {
        key: 'loanScenarios',
        label: 'Manage Loan Scenarios',
        href: '/loanScenarios',
        permissions: ['LoanScenarios'],
        pages: ['manage_users', 'loanScenarios'],
      },
      {
        // One directory for everyone who puts money into a loan or a fund. It replaced the separate
        // Manage Investors and Manage Participants entries, so either old permission opens it.
        key: 'manageInvestors',
        label: 'Manage Investors',
        href: '/manageInvestors',
        permissions: ['Investors.Purchases', 'Investors.Servicing'],
        pages: ['manage_users', 'manageInvestors'],
      },
      {
        // Investor onboarding has no entry of its own: an application is the way into a fund, so the
        // queue is that fund's Onboarding tab. /manageInvestorOnboarding still resolves for anyone
        // who bookmarked the fund-independent view.
        key: 'mortgageFunds',
        label: 'Mortgage Funds',
        href: '/mortgageFunds',
        permissions: ['Funds'],
        pages: ['manage_users', 'mortgageFunds'],
      },
      {
        key: 'ratesheet',
        label: 'Rate Sheet',
        href: '/ratesheet',
        permissions: ['RateSheet'],
        pages: ['manage_users', 'ratesheet'],
      },
      {
        key: 'borrowingBases',
        label: 'Borrowing Bases',
        href: '/manageBorrowingBases',
        permissions: ['BorrowingBases'],
      },
      {
        key: 'editProfile',
        label: 'Edit Profile & Info',
        href: '/edit_profile',
        visible: true,
      },
      {
        key: 'tickets',
        label: 'Submit a Ticket',
        href: '/tickets',
        permissions: ['AdminTools'],
        visible: IS_MAIN_COMPANY ? false : undefined,
      },
      {
        key: 'accounting',
        label: 'Accounting',
        href: '/accountings/accountingDashboard',
        permissions: ['Accounting'],
        pages: ['manage_accountings'],
      },
      {
        key: 'billing',
        label: 'Billing',
        href: '/billing',
        permissions: ['Billing'],
      },
      {
        key: 'mails',
        label: 'Mail',
        href: '/mails',
        permissions: ['Mail'],
        pages: ['manage_mails'],
      },
      {
        key: 'calendar',
        label: 'Calendar',
        href: '/calendar',
        permissions: ['Calendar'],
      },
      {
        key: 'aiProjects',
        label: 'AI Projects',
        href: '/ai-projects',
        permissions: ['AiProjects'],
      },
      {
        key: 'vendors',
        label: 'Vendors',
        href: '/vendors/creditReport',
        permissions: ['Vendors'],
        pages: ['vendors'],
      },
      {
        key: 'conditionTemplates',
        label: 'Conditions & Templates',
        href: '/condition_template/conditions',
        permissions: ['Template'],
        pages: ['condition_template', 'conditions'],
      },
      {
        key: 'adminTools',
        label: 'Admin Tools',
        href: '/admin_tool/permissions',
        permissions: [
          'AdminTools',
          'AdminTools.Permissions',
          'AdminTools.AppraiserSettlementContractor',
          'AdminTools.Reviews',
        ],
        pages: ['admin_tool', 'permissions'],
      },
      {
        key: 'userActivity',
        label: 'User Activity',
        href: '/user_activity',
        permissions: ['User.Activity'],
        pages: ['manage_users', 'user_activity'],
      },
    ]

    // The company's own settings for this dropdown: label, link, order and whether the row is
    // offered at all. The permission each row needs stays above, keyed on `key` - a saved config
    // cannot express it, and an admin must not be able to hand out a screen by renaming a row.
    const draftMenus = menuConfig
      .map((item, index) => {
        const config: any = accountMenuConfig[item.key]
        return {
          key: item.key,
          label: config?.title || item.label,
          href: config?.link || item.href,
          external: (item as any).external ?? false,
          order: config?.order ?? index + 1,
          visible:
            config?.value === false
              ? false
              : item.visible ??
                ((!item.permissions || item.permissions.some((p) => hasPermission(p))) &&
                  (!item.pages || hasPagePermissions(item.pages))),
        }
      })
      .sort((a, b) => a.order - b.order)

    // A rule above Sign Out, which HeaderMenu renders itself.
    setMenus([...draftMenus, { label: 'divider', href: '#', visible: true }])
  }, [permissionData, pagePermissions, hasPermission, hasPagePermissions, accountMenuConfig])

  const onChangeLoginForm = (key: string, value: string) => {
    let temp: any = cloneDeep(fields)
    temp[key] = value
    setFields(temp)
  }

  const queryClient = useQueryClient()

  const handleStopImpersonate = async () => {
    try {
      const response = await stopImpersonate()
      await applyImpersonationSession(dispatch, queryClient, response)
      redirectAfterImpersonationIfNeeded(navigate, response.user)
    } catch {
      toast.error('Unable to return to your account')
    }
  }

  const goToLandingAfterLogin = async () => {
    setFields({
      email: '',
      password: '',
    })
    if (location.state?.from) {
      navigate.push(location.state.from)
    } else if (!navigate.location.pathname.includes('/loan_process/structure/new')) {
      await delay(0.5)
      navigate.push('/pipeline')
    }
  }

  const submitLogin = async () => {
    let errors = 0
    fields.email = fields.email.trim().toLowerCase()
    if (!validateEmail(fields.email)) errors += 1
    if (fields.password.length === 0) errors += 1
    if (errors) {
      return toast('Invalid Email Address or Password!', { type: 'error' })
    }
    setAction('Signing')
    await queryClient.clear()
    props.signInRequest(
      fields,
      async (
        isPass2fa: boolean,
        phone: string,
        requireReset: boolean,
        requirePwChange: boolean,
        hasPasskey: boolean,
      ) => {
        setAction('')
        if (!isPass2fa) {
          set2fa(true)
          setPhone(phone)
          setHasPasskey(hasPasskey)
          return
        }
        if (requireReset || requirePwChange) {
          setShowResetPasswordModal(true)
        } else {
          await goToLandingAfterLogin()
        }
      },
      () => {
        setAction('')
      },
    )
  }

  const keyPress = (e: any) => {
    if (e.keyCode === 13 || e.keyCode === 9) {
      submitLogin()
      e.preventDefault()
    }
  }

  const renderLoginForm = () => {
    if (!IS_MAIN_COMPANY) return null
    if (auth.isAuthenticated) return null
    return (
      <div className="login-form-container">
        <div className="flex justify-end flex-wrap gap-2 md:pr-4 mt-2">
          <Input
            type="text"
            title="Email"
            value={fields.email}
            onChange={(value) => onChangeLoginForm('email', value)}
            className="-mb-4 w-[240px]"
          />
          <Input
            type="password"
            title="Password"
            value={fields.password}
            onChange={(value) => onChangeLoginForm('password', value)}
            className="-mb-4 w-[240px]"
            onKeyDown={(e) => keyPress(e)}
          />
          <div className="flex items-center" onClick={submitLogin}>
            <div className="hover:underline text-shade-blue cursor-pointer flex flex-wrap text-[14px] items-center gap-1">
              <UserIcon className="w-3 h-3"></UserIcon>
              <span className="font-bold">{`LogIn`}</span>
              <ArrowRightIcon className="w-3 h-4"></ArrowRightIcon>
              {action === 'Signing' && <img src={svgLoading} className="inline w-4 h-4 ml-2 text-white animate-spin" />}
            </div>
          </div>
        </div>
        <div className="flex justify-end md:pr-4">
          <Link to="/forgetPassword">
            <p className="block mt-1 text-[13px] hover:text-red-900 hover:underline text-red-800">
              Forgot/Reset your password?
            </p>
          </Link>
        </div>
        {is2fa && (
          <TfaModal
            email={fields.email}
            phone={phone}
            hasPasskey={hasPasskey}
            password={fields.password}
            onClose={() => set2fa(false)}
            onVerified={(res: any) => {
              if (res?.requirePwChange || res?.user?.requireReset) {
                setShowResetPasswordModal(true)
              } else {
                goToLandingAfterLogin()
              }
            }}
          />
        )}
      </div>
    )
  }

  const handleLoginModalViewed = () => {
    dispatch({
      type: USER_LOGIN_PAGE_MODAL_VIEWED,
    })
  }

  const renderLogo = () => {
    const content = (
      <>
        <span className="sr-only">{COMPANY_NAME}</span>
        <LogoBlue />
      </>
    )
    const customLink = _menu?.logos?.[auth.isAuthenticated ? 'brokerPortal' : 'homePortal']
    if (customLink) {
      return <a href={convertUrl(customLink)}>{content}</a>
    }
    return <Link to="/">{content}</Link>
  }

  return (
    <>
      {companyBroadcast && (
        <div className="bg-red-100 text-red-900 font-bold w-full p-3 text-center">{companyBroadcast}</div>
      )}
      {auth.isAuthenticated && auth.role?.impersonatedBy && (
        <div className="bg-sky-50 text-sky-900 w-full px-3 py-2 text-center text-sm">
          Viewing as <span className="font-semibold">{auth.profile?.name}</span>
          {' · '}
          <button type="button" className="font-semibold underline hover:text-sky-700" onClick={handleStopImpersonate}>
            Return to {auth.role.impersonatedByName || 'your account'}
          </button>
        </div>
      )}

      {/* No backdrop-blur / z-index here: backdrop-filter (or position+z-index)
          creates a stacking context that traps the nav/profile dropdowns behind
          page sections. A plain background keeps the dropdowns above the page. */}
      <div className="bg-white border-b border-line shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
        <div className="max-w-screen-2xl m-auto relative py-3.5">
          <nav className="relative flex items-center justify-between" aria-label="Global">
            <div className="flex items-center flex-grow flex-shrink-0 lg:flex-grow-0">
              <div className="flex items-center justify-between gap-3 lg:gap-4 w-full lg:w-auto">
                {renderLogo()}
                {auth.isAuthenticated && (
                  <div className="hidden lg:block flex-shrink-0">
                    <PipelineModeSwitch />
                  </div>
                )}
                {auth.isAuthenticated && (
                  <div className="hidden lg:block flex-shrink-0 max-w-[10rem]">
                    <GlobalLoanSearch className="w-full" />
                  </div>
                )}
                {auth.isAuthenticated && (
                  <div className="hidden lg:block flex-shrink-0">
                    <LoanFieldLookup />
                  </div>
                )}
                <div className="mr-2 flex items-center lg:hidden">
                  <div
                    className="bg-white rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100 hover:outline-none hover:ring-2 hover:ring-inset hover:ring-primary"
                    onClick={() => setShowMobileMenu(true)}
                  >
                    <span className="sr-only">Open main menu</span>
                    <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                  </div>
                </div>
              </div>
            </div>
            <div className="hidden items-center lg:flex lg:flex-1 lg:gap-4 lg:justify-end min-w-0">
              <HeaderNav items={navItems} />
              {auth.isAuthenticated && (
                <div className="flex items-center gap-3 lg:gap-4 flex-shrink-0">
                  {hasPermission('User.Impersonate') === true && (
                    <div className="hidden lg:block flex-shrink-0">
                      <GlobalUserSwitch />
                    </div>
                  )}
                  <div className="flex header-profile-name text-gray-600">
                    <UserIcon className="w-5 h-5"></UserIcon>
                    <span className="font-normal ml-1 text-[15px]">{signedInName(auth)}</span>
                  </div>
                  <HeaderMenu items={menus} onSelect={(href) => navigate.push(href)} onLogout={handleLogout} />
                  {/* Last in this group, so the bell stays level with the hamburger. */}
                  {notificationsEnabled && <NotificationBell />}
                </div>
              )}
            </div>
          </nav>
          {renderLoginForm()}
          {auth.isAuthenticated && !auth.loginPageModalShowed && logInAlerts.length > 0 && (
            <LogInAlertModal data={logInAlerts} onClose={() => handleLoginModalViewed()} />
          )}
        </div>
        {showResetPasswordModal && (
          <ResetPasswordModal
            isOpen={showResetPasswordModal}
            onClose={(completed?: boolean) => {
              setShowResetPasswordModal(false)
              if (completed) {
                navigate.push('/pipeline')
              } else {
                dispatch(logout())
                navigate.push('/login')
              }
            }}
            email={fields.email}
          />
        )}
        {showMobileMenu && (
          <div>
            <div className="absolute z-50 top-0 inset-x-0 p-2 transition transform origin-top-right lg:hidden">
              <div className="rounded-lg shadow-md bg-white ring-1 ring-black ring-opacity-5 overflow-hidden">
                <div className="px-5 pt-4 flex items-center justify-between">
                  <div>
                    <LogoBlue />
                  </div>
                  <div className="-mr-2">
                    <div
                      className="bg-white rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100 hover:outline-none hover:ring-2 hover:ring-inset hover:ring-primary"
                      onClick={() => setShowMobileMenu(false)}
                    >
                      <span className="sr-only">Close main menu</span>
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </div>
                  </div>
                </div>
                <div className="px-2 pt-2 pb-3 space-y-1">
                  {Object.keys(navigations).map((key, index) => {
                    const item: any = navigations[key]
                    if (item.visible && menu[key]?.value) {
                      return renderNav(
                        item,
                        `${index}`,
                        `block px-3 py-2 rounded-md text-base text-gray-600 font-bold hover:text-gray-900 hover:bg-gray-50 ${activeMenu(
                          item.href,
                        )}`,
                      )
                    }
                  })}
                  {auth.isAuthenticated && (
                    <div>
                      <div className="block rounded h-px bg-gray-100"></div>
                      <div className="px-3 py-3">
                        <GlobalLoanSearch className="w-full" />
                      </div>
                      <PipelineModeSwitch variant="list" onSwitched={() => setShowMobileMenu(false)} />
                      <div className="block rounded h-px bg-gray-100"></div>
                      {menus.map(
                        (item, index) =>
                          item.visible &&
                          (item.label === 'divider' ? (
                            <div className="block rounded h-px bg-gray-100" key={index}></div>
                          ) : item.external ? (
                            <a
                              key={`trans-menu-${index}`}
                              href={item.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block px-3 py-2 rounded-md text-base text-gray-600 font-bold hover:text-gray-900 hover:bg-gray-50"
                            >
                              {item.label}
                            </a>
                          ) : (
                            <Link
                              key={`trans-menu-${index}`}
                              to={item.href}
                              className={`block px-3 py-2 rounded-md text-base text-gray-600 font-bold hover:text-gray-900 hover:bg-gray-50 ${activeMenu(
                                item.href,
                              )}`}
                            >
                              {item.label}
                            </Link>
                          )),
                      )}
                      <div
                        className="block px-3 py-2 rounded-md text-base text-gray-600 font-bold hover:text-gray-900 hover:bg-gray-50"
                        onClick={handleLogout}
                      >
                        Sign Out
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Rendered outside the backdrop-blur header: backdrop-filter creates a
          containing block for position:fixed, which would break the FAB's anchoring.
          Signed-out pages (login, register, reset password) render the Header too, so
          gate the assistant on auth - its endpoints are JWT-only and would just 401. */}
      {auth.isAuthenticated && <AssistantModal context="resources-assistant" />}
    </>
  )
}

function mapStateToProps() {
  return {}
}

export const Header = connect(mapStateToProps, { signInRequest })(_Header)
